import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getQQAgentInvocations } from 'lib/api/agents/getQQAgentInvocations';
import { Maybe } from 'types/UtilityTypes';
import {
  AgentInvocationStatus,
  InvocationRuleSetSummary,
  QQAgentEngine,
  QQAgentInvocation,
  QuickQuoteSlim,
} from 'types/agents/QuickQuoteAgent';

const MAX_POLL_ATTEMPTS = 10; // 30 seconds / 3 seconds per attempt
const POLL_INTERVAL = 3000; // 3 seconds

type UseQQAgentInvocationsParams = {
  threadId: Maybe<string>;
  enabled: boolean;
  // Written-rules agents: keep failed runs visible so the card can say what went wrong.
  showFailedLLMInvocations?: boolean;
};

type UseQQAgentInvocationsReturn = {
  invocations: QQAgentInvocation[];
  quickQuotesByInvocationId: Record<string, QuickQuoteSlim[]>;
  ruleSetsByInvocationId: Record<string, InvocationRuleSetSummary>;
  loading: boolean;
  error: Maybe<string>;
  showAgentSection: boolean;
};

export const useQQAgentInvocations = ({
  threadId,
  enabled,
  showFailedLLMInvocations = false,
}: UseQQAgentInvocationsParams): UseQQAgentInvocationsReturn => {
  const [invocations, setInvocations] = useState<QQAgentInvocation[]>([]);
  const [quickQuotesByInvocationId, setQuickQuotesByInvocationId] = useState<
    Record<string, QuickQuoteSlim[]>
  >({});
  const [ruleSetsByInvocationId, setRuleSetsByInvocationId] = useState<
    Record<string, InvocationRuleSetSummary>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Maybe<string>>(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  // Use ref to track poll timer to avoid stale closures
  const pollTimerRef = useRef<Maybe<NodeJS.Timeout>>(null);
  // Use ref to track if this is the initial fetch to avoid React batching issues
  const isInitialFetchRef = useRef(true);
  const hasReceivedDataRef = useRef(false);
  // Track if the check for all-failed has been performed
  const hasPerformedInitialCheckRef = useRef(false);
  // AbortController to cancel in-flight requests when threadId changes
  const abortControllerRef = useRef<Maybe<AbortController>>(null);

  const hasInProgressInvocations = useMemo(() => {
    return invocations.some(
      (inv) => inv.status === AgentInvocationStatus.InProgress
    );
  }, [invocations]);

  const fetchInvocations = useCallback(
    async (isPolling = false) => {
      if (!threadId || !enabled) {
        setInvocations([]);
        setQuickQuotesByInvocationId({});
        setRuleSetsByInvocationId({});
        setError(null);
        setLoading(false);
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Only show loading spinner on initial fetch, not during polling
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await getQQAgentInvocations({
          threadId,
        });

        // Check if this request was aborted (stale)
        if (abortController.signal.aborted) {
          return;
        }

        if (!result.isOk()) {
          // On polling errors, preserve existing invocations
          if (isPolling) {
            setError(result.error.message);
          } else {
            // On initial fetch errors, clear invocations
            setInvocations([]);
            setQuickQuotesByInvocationId({});
            setRuleSetsByInvocationId({});
            setError(result.error.message);
          }
          return;
        }

        setInvocations(result.value.invocations);
        setQuickQuotesByInvocationId(
          result.value.quickQuotesByInvocationId ?? {}
        );
        setRuleSetsByInvocationId(result.value.ruleSetsByInvocationId ?? {});
        setError(null);

        // Mark that we've received data (for initial fetch logic)
        if (!isPolling && !hasReceivedDataRef.current) {
          hasReceivedDataRef.current = true;
        }
      } catch {
        // Check if this request was aborted (stale)
        if (abortController.signal.aborted) {
          return;
        }

        // On polling errors, preserve existing invocations
        if (isPolling) {
          setError('Failed to fetch agent invocations');
        } else {
          // On initial fetch errors, clear invocations
          setInvocations([]);
          setQuickQuotesByInvocationId({});
          setRuleSetsByInvocationId({});
          setError('Failed to fetch agent invocations');
        }
      } finally {
        if (!isPolling && !abortController.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [threadId, enabled]
  );

  const showAgentSection = useMemo(() => {
    if (!enabled) {
      return false;
    }

    // If no invocations, don't show
    if (invocations.length === 0) {
      return false;
    }

    // If this is the initial fetch and all invocations are failed, don't show
    // Use ref to avoid React batching issues with state updates
    // Only check once to avoid multiple evaluations in Strict Mode
    if (
      isInitialFetchRef.current &&
      hasReceivedDataRef.current &&
      !hasPerformedInitialCheckRef.current
    ) {
      const allFailed = invocations.every(
        (inv) =>
          inv.status === AgentInvocationStatus.Failed &&
          !(showFailedLLMInvocations && inv.engine === QQAgentEngine.LLM)
      );
      if (allFailed) {
        return false;
      }
    }

    return true;
  }, [enabled, invocations, showFailedLLMInvocations]);

  // Separate effect to mark initial check as complete (pure useMemo)
  useEffect(() => {
    if (
      isInitialFetchRef.current &&
      hasReceivedDataRef.current &&
      !hasPerformedInitialCheckRef.current &&
      invocations.length > 0
    ) {
      hasPerformedInitialCheckRef.current = true;
      isInitialFetchRef.current = false;
    }
  }, [invocations]);

  // Initial fetch when threadId or enabled changes
  useEffect(() => {
    // Cancel any in-flight request when threadId changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    isInitialFetchRef.current = true;
    hasReceivedDataRef.current = false;
    hasPerformedInitialCheckRef.current = false;
    setPollAttempts(0);
    fetchInvocations();

    // Cleanup: cancel request if component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [threadId, enabled, fetchInvocations]);

  // Reset poll attempts when invocations complete or fail
  useEffect(() => {
    if (!hasInProgressInvocations && pollAttempts > 0) {
      setPollAttempts(0);
    }
  }, [hasInProgressInvocations, pollAttempts]);

  // Polling effect for in-progress invocations
  useEffect(() => {
    // Clear any existing timer
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    // Don't poll if no in-progress invocations or max attempts reached
    if (!hasInProgressInvocations || pollAttempts >= MAX_POLL_ATTEMPTS) {
      return;
    }

    // Set up next poll
    pollTimerRef.current = setTimeout(() => {
      fetchInvocations(true);
      setPollAttempts((prev) => prev + 1);
    }, POLL_INTERVAL);

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasInProgressInvocations, pollAttempts, fetchInvocations]);

  return {
    invocations,
    quickQuotesByInvocationId,
    ruleSetsByInvocationId,
    loading,
    error,
    showAgentSection,
  };
};
